"use client"

import styled from "@emotion/styled"
import { useTranslations } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  extractProperty,
  FacilityItem,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
  PortItem,
  UmbracoImageType,
} from "utils"
import PortTitle from "./PortTitle"
import { useIsDesktop } from "./common/bit/MediaQueries"
import { isVideo, ResponsiveMedia } from "./common/bit/MediaRenderer"

// const TRANSITION = `all 0.36s cubic-bezier(0.34, 1.56, 0.64, 1)`
const TRANSITION = `all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)`
const StyledSection = styled.section<{ backgroundColor?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 40px;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 90px 16px 180px;
  }
`

const StyledContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const StyledPortList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  margin-top: 40px;

  width: 100%;
  display: grid;
  grid-template-columns: 100%;
  grid-auto-rows: 267px;

  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: 501px;
  }
`
const StyledPortListItem = styled.li`
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
    ${(props) => props.theme.breakpoints.up("md")} {
      background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
    }
  }
  &:hover,
  &:focus-within {
    ${(props) => props.theme.breakpoints.up("md")} {
      .facility-list {
        --scroll-height: 24px;
        height: var(--scroll-height);
        width: 100%;
      }
      .port-content-wrapper {
        padding-inline-start: 32px;
        padding-bottom: 24px;
      }
      .port-content {
        padding-bottom: 0;
      }
      .port-desktop-link {
        opacity: 1;
      }
    }
  }
`

const StyledPortMedia = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  > div {
    width: 100%;
    height: 100%;
  }

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const StyledPortContentWrapper = styled.div`
  position: absolute;
  z-index: 3;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 100%;
  padding: 24px;
  transition: ${TRANSITION};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 24px 12px;
    grid-template-columns: 1fr auto;
    gap: 22px;
  }
`

const StyledDesktopLinkButton = styled.div`
  cursor: pointer;
  display: none;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 45px;
  width: max-content;
  height: max-content;
  align-self: flex-end;
  justify-self: flex-end;
  color: white;
  border: 1px solid white;
  background-color: rgba(14, 40, 70, 0.2);
  transition: background-color 0.7s ease, opacity 0.7s ease;
  opacity: 0;
  &:hover,
  &:focus-within {
    background-color: rgba(14, 40, 70, 0.3);
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
  }
`
const StyledPortContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  transition: ${TRANSITION};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 8px;
  }
`

const StyledItemTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 32px;
  font-weight: 400;
  line-height: 125%; /* 40px */
  text-transform: capitalize;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const StyledFacilitiesList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    overflow: hidden;
    height: 0;
    margin-top: 0;
    transition: ${TRANSITION};
  }
`
const StyledFacilityItem = styled.li`
  color: white;
  list-style: none;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  span {
    font-size: 16px;
    font-weight: 400;
    line-height: normal;
  }
  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    span {
      font-size: 18px;
    }
    img {
      width: 24px;
      height: 24px;
    }
  }
`

type PortOurLocationsProps = {
  data: any
  ports?: PortItem[]
}

type FacilityProps = {
  facilityList: FacilityItem[]
}

const FacilitiesList = ({ facilityList }: FacilityProps) => {
  const facilityListRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const calculateScrollHeight = () => {
      if (facilityListRef?.current) {
        const scrollHeight = facilityListRef.current?.scrollHeight || 0
        facilityListRef.current.style.setProperty("--scroll-height", `${scrollHeight}px`)
      }
    }

    window.addEventListener("resize", calculateScrollHeight)
    calculateScrollHeight()

    return () => {
      window.removeEventListener("resize", calculateScrollHeight)
    }
  }, [])

  return (
    <StyledFacilitiesList className="facility-list" ref={facilityListRef}>
      {facilityList.map(
        (facility, idx) =>
          (facility.facilityName || facility.icon) && (
            <StyledFacilityItem key={idx}>
              {facility.icon && (
                <Image
                  src={getUmbracoMediaUrl(facility.icon)}
                  alt={facility.facilityName || ""}
                  width={24}
                  height={24}
                />
              )}
              {facility.facilityName && <span>{facility.facilityName}</span>}
            </StyledFacilityItem>
          ),
      )}
    </StyledFacilitiesList>
  )
}

const StyledUpperTextOurLocation = styled.p`
  margin: 0;
  padding: 0;
  color: white;
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  margin-bottom: 4px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 0;
  }
`

type MediaItemProps = {
  desktopImage?: UmbracoImageType | null
  mobileImage?: UmbracoImageType | null
  portName?: string
  isHovered?: boolean
  isFocused?: boolean
}
const MediaItem = ({
  desktopImage,
  mobileImage,
  portName,
  isHovered,
  isFocused,
}: MediaItemProps) => {
  const isDesktop = useIsDesktop()
  const desktopVideoRef = useRef<HTMLVideoElement>(null)
  const mobileVideoRef = useRef<HTMLVideoElement>(null)
  const hasVideoMedia =
    (isDesktop && isVideo(desktopImage?.url)) || (!isDesktop && isVideo(mobileImage?.url))

  let altText = isDesktop ? desktopImage?.altText : mobileImage?.altText

  if (!altText) {
    altText = typeof portName === "string" && portName.length ? portName : "Port Image"
  }

  // Play video on desktop hover or mobile focus
  useEffect(() => {
    if (!hasVideoMedia) return

    const shouldPlay = isHovered || isFocused
    ;[desktopVideoRef.current, mobileVideoRef.current].forEach((video) => {
      if (!video) return
      if (shouldPlay) {
        video.currentTime = 0
        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => undefined)
        }
      } else {
        video.pause()
        video.currentTime = 0
      }
    })
  }, [isHovered, isFocused, hasVideoMedia])

  return (
    <ResponsiveMedia
      desktopMedia={getUmbracoMediaUrl(desktopImage?.url || "")}
      mobileMedia={getUmbracoMediaUrl(mobileImage?.url || "")}
      alt={altText}
      desktopVideoRef={desktopVideoRef}
      mobileVideoRef={mobileVideoRef}
    />
  )
}

type PortListItemProps = {
  mobileImage?: UmbracoImageType | null
  desktopImage?: UmbracoImageType | null
  portName?: string
  url: string
  facilityList: FacilityItem[]
  learnMoreText: string
  upperTextOurLocation?: string
}

const PortListItem = ({
  mobileImage,
  desktopImage,
  portName,
  url,
  facilityList,
  learnMoreText,
  upperTextOurLocation,
}: PortListItemProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const handleMouseEnter = useCallback(() => setIsHovered(true), [])
  const handleMouseLeave = useCallback(() => setIsHovered(false), [])
  const handleFocus = useCallback(() => setIsFocused(true), [])
  const handleBlur = useCallback(() => setIsFocused(false), [])

  return (
    <StyledPortListItem onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <StyledLink
        href={getNonUmbracoUrl(url)}
        prefetch={false}
        aria-label={portName}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <StyledPortMedia>
          <MediaItem
            desktopImage={desktopImage || undefined}
            mobileImage={mobileImage || undefined}
            portName={portName}
            isHovered={isHovered}
            isFocused={isFocused}
          />
        </StyledPortMedia>
        <StyledPortContentWrapper className="port-content-wrapper">
          <StyledPortContent className="port-content">
            {upperTextOurLocation && (
              <StyledUpperTextOurLocation>{upperTextOurLocation}</StyledUpperTextOurLocation>
            )}
            {portName && <StyledItemTitle>{portName}</StyledItemTitle>}
            {facilityList.length > 0 && <FacilitiesList facilityList={facilityList} />}
          </StyledPortContent>
          <StyledDesktopLinkButton className="port-desktop-link">
            {learnMoreText}
          </StyledDesktopLinkButton>
        </StyledPortContentWrapper>
      </StyledLink>
    </StyledPortListItem>
  )
}

const PortOurLocations = ({ data, ports }: PortOurLocationsProps) => {
  const t = useTranslations("PortOurLocations")
  const id = extractProperty(data, "id") || "port-our-locations"
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")
  const subtitle = extractProperty(data, "subtitle")

  const filteredPorts: PortItem[] = useMemo(() => {
    const itemsList = extractProperty(data, "itemsList") || []
    const normalizeUrl = (url: string) =>
      decodeURIComponent(url || "").replace(/\/+$/, "").toLowerCase()
    return (
      itemsList
        ?.map((item: any) => {
          const itemUrl = normalizeUrl(item.url)
          return ports?.find((port) => {
            const portUrl = normalizeUrl(port.url)
            return portUrl === itemUrl || portUrl.includes(itemUrl) || itemUrl.includes(portUrl)
          })
        })
        .filter(Boolean) || []
    )
  }, [ports, data])

  // in case data fields are empty, return null
  if (!blackTitle && !blueTitle && !subtitle && filteredPorts.length === 0) {
    return null
  }

  return (
    <StyledSection id={id}>
      <StyledContentWrapper>
        <PortTitle
          normalText={blackTitle}
          highlightedText={blueTitle}
          centered
          subtitle={subtitle}
        />
        <StyledPortList>
          {filteredPorts.map(
            (
              { mobileImage, desktopImage, portName, url, facilityList, upperTextOurLocation },
              index,
            ) => (
              <PortListItem
                key={index}
                mobileImage={mobileImage || undefined}
                desktopImage={desktopImage || undefined}
                portName={portName}
                url={url}
                facilityList={facilityList}
                learnMoreText={t("learnMore")}
                upperTextOurLocation={upperTextOurLocation}
              />
            ),
          )}
        </StyledPortList>
      </StyledContentWrapper>
    </StyledSection>
  )
}
export default PortOurLocations
